import React from 'react';
import i18n from 'i18n-js';
import { isWeb, isPhone } from './platform';
import { AlertTextRenderFunction } from '@components/alert/types';

/* eslint-disable no-var */
declare var toast: { show: (message: string, options: Record<string, unknown>) => void };
declare var systemAlert: { show: (config: Record<string, unknown>) => void; hide: () => void };
declare var systemFeedback: { show: (config: Record<string, unknown>) => void };
declare var systemPopup: { show: (config: Record<string, unknown>) => void; hide: (id?: string) => void };
declare var systemImageUpload: { show: (config: Record<string, unknown>) => void; browse: (config: Record<string, unknown>) => void };
/* eslint-enable no-var */

export const Alert = ({
  title = '',
  titleColor = null,
  text = '',
  actions = null,
  type = 'normal',
  position = '',
  boldPrimary = false,
  style = {},
  loadingIndicator = false,
}: {
  title?: string;
  titleColor?: string | null;
  text?: string | React.ReactNode | AlertTextRenderFunction;
  actions?: Array<{ text: string; style?: string; onPress?: () => void; testID?: string }> | null;
  type?: string;
  position?: string;
  boldPrimary?: boolean;
  style?: Record<string, unknown>;
  loadingIndicator?: boolean;
}) => {
  if (actions) {
    systemAlert.show({ title, titleColor, text, actions, position, boldPrimary, loadingIndicator });
  } else {
    toast.show(`${title}${text ? `\n${text}` : ''}`, {
      type: type,
      placement: position || (isWeb ? 'bottom' : 'top'),
      duration: 4000,
      offsetTop: 500,
      offset: 500,
      animationType: 'slide-in',
      style,
    });
  }
};

export const Feedback = (config: Record<string, unknown>) => {
  systemFeedback.show(config);
};

export const Popup = (config: Record<string, unknown> & { showPopup?: boolean; id?: string }) => {
  const { showPopup = true } = config;
  if (showPopup) {
    systemPopup.show({ ...config, showPopup });
  } else {
    systemPopup.hide(config.id);
  }
};

export const ImageUpload = (config: Record<string, unknown>) => {
  systemImageUpload.show(config);
};

export const DocumentBrowse = (config: Record<string, unknown>) => {
  systemImageUpload.browse(config);
};

export const bottomAnimationConfig = {
  animationEnabled: !isWeb,
  gestureDirection: 'vertical',
  cardStyleInterpolator: ({
    current,
    layouts,
  }: {
    current: { progress: { interpolate: (config: { inputRange: number[]; outputRange: number[] }) => unknown } };
    layouts: { screen: { height: number } };
  }) => ({
    cardStyle: {
      transform: [
        {
          translateY: current.progress.interpolate({
            inputRange: [0, 1],
            outputRange: [layouts.screen.height, 0],
          }),
        },
      ],
    },
  }),
};

export const getLinkingModalConfig = (scale: (size: number) => number) => {
  return {
    width: ({ fullWidth }: { fullWidth: number }) => Math.min(fullWidth - scale(isWeb && fullWidth > 1116 ? 36 : 0), 1280),
    offsetBottom: scale(18),
    offsetTop: isPhone ? scale(53) : isWeb ? scale(62) : scale(36),
    useFullHeight: true,
    avoidingViewEnable: false,
  };
};

export const triggerRemovePI = async ({
  setActionsModalOpen,
  setIsRemovingPI,
  removeProductionInformation,
  params,
  onRemove = () => {},
}: {
  setActionsModalOpen: (open: boolean) => void;
  setIsRemovingPI: (removing: boolean) => void;
  removeProductionInformation: (params: unknown) => Promise<unknown>;
  params: unknown;
  onRemove?: () => void;
}) => {
  setActionsModalOpen(false);
  Alert({
    title: i18n.t('Are you sure?'),
    text: `${i18n.t('You are about to remove all existing production information.')}\n\n${i18n.t(
      'After removing, you can add new production information or mark the nc file as Ignored.'
    )}`,
    actions: [
      {
        // Mirror of TEST_IDS.alert.cancel in components/v2/pages/common/constants/base.constants.ts.
        // Inlined as a literal to avoid a circular import (base.constants.ts imports from helpers.ts).
        testID: 'alert-action-cancel',
        text: i18n.t('Cancel'),
        onPress: () => {},
        style: 'primary',
      },
      {
        // Mirror of TEST_IDS.alert.confirmRemove — see comment above.
        testID: 'alert-action-confirm-remove',
        text: i18n.t('Remove'),
        style: 'danger',
        onPress: async () => {
          setIsRemovingPI(true);
          await removeProductionInformation(params);
          onRemove?.();
          setIsRemovingPI(false);
        },
      },
    ],
  });
};

export const deletePrompt = ({
  images,
  selectedIndex,
  setSelectedIndex,
  deleteImage,
  onSuccessImageRemove,
  imageContext,
}: {
  images: Array<Record<string, unknown>>;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  deleteImage: (params: Record<string, unknown>) => void;
  onSuccessImageRemove?: (documentTag: unknown) => void;
  imageContext: unknown;
}) => {
  const image = images[selectedIndex];
  if (!image) {
    return;
  }
  Alert({
    title: 'Are you sure?',
    actions: [
      {
        text: 'No',
        onPress: () => console.log('Cancel Pressed'),
        style: 'primary',
        // Inlined to avoid a circular import (base.constants.ts imports from helpers.ts).
        // Mirror of TEST_IDS.alert.cancel.
        testID: 'alert-action-cancel',
      },
      {
        text: 'Yes',
        onPress: () => {
          const isLast = selectedIndex === images.length - 1;
          if (isLast && selectedIndex !== 0) {
            setSelectedIndex(selectedIndex - 1);
          }
          onSuccessImageRemove?.(image?.documentTag);
          deleteImage({ imageContext, imageDocument: image, documentId: image.documentId });
        },
        // Mirror of TEST_IDS.alert.confirmRemove — see comment above.
        testID: 'alert-action-confirm-remove',
      },
    ],
  });
};
